import { httpsCallable } from 'firebase/functions';
import type { MfaEnrollAuthorizationPayload, MfaEnrollAuthorizationResponse } from '@usacsmideo/types';
import { functions } from '../../constants/firebaseModular';

/**
 * THE MFA ENROLL-AUTHORIZATION CHECKPOINT — C3 (CU-86ajwpnek, porting
 * CU-86ajva3wz / CU-86ajnb1qh).
 *
 * One call, one job: trade a fresh-reauth proof for a short-lived, single-use,
 * uid-bound enroll-authorization token. Nothing else — the IdP ceremony itself
 * goes client→Identity Platform directly and never touches our backend.
 *
 * ⚠️ THE TOKEN IS EVIDENCE, NOT ENFORCEMENT. `multiFactor().enroll()` hits IdP
 * directly and Firebase exposes NO enroll-time blocking trigger, so this token
 * cannot PREVENT a planted factor — there is nowhere in IdP's enrollment API to
 * present it. It establishes the server-side record against which the scheduled
 * reconciliation sweep DETECTS a factor enrolled without a matching
 * authorization. Do not build client-side enforcement around it.
 *
 * ⚠️ THE CALLER DISCARDS THE RETURNED TOKEN, AND THAT IS CORRECT. `enrollAuthToken`
 * has no client-side use — nothing in the IdP ceremony accepts it. What matters
 * is that the call SUCCEEDED, because that is what wrote the server-side record.
 * This looks exactly like a pointless call whose result is thrown away, and it
 * is not: deleting it silently converts every future portal enrollment into
 * `reconcile.unmatched-factor` — the attacker's signature — on a real patient's
 * account. If you are tempted to "optimise" this away, read the sweep first.
 *
 * ⚠️ WHY THE CEREMONY CALLS THIS AT ALL. Enrolling a factor by any path that
 * bypasses the checkpoint produces EXACTLY the attacker's signature — a factor at
 * IdP with no `authorization.minted`, nothing for reconciliation to match,
 * flagged `reconcile.unmatched-factor` indefinitely. The detector is correct; it
 * cannot distinguish "enrolled through an unofficial path" from "attacker with a
 * hijacked session", because both bypass the checkpoint identically. This
 * wrapper is what makes a portal enrollment matchable.
 *
 * NO BACKEND CHANGE WAS NEEDED — verified 2026-08-07 against
 * `mideo-cloud-functions`, not assumed: `mfaEnrollAuthorization` is declared
 * `onCall({ cors: CORS_PORTAL_APP })` (`src/auth/mfa/mfaEnrollAuthorization.ts:77-78`),
 * and that profile lists `LOCALHOST_PORTAL_APP` (:5174, the portal's dev port),
 * `STAGING_PORTAL`/`STAGING_PORTAL_ALT` and `PROD_PORTAL`/`PROD_PORTAL_ALT`
 * (`src/constants/cors.ts:57-69`). The callable also has NO role check and binds
 * to `req.auth.uid`, so it serves a patient uid unchanged.
 *
 * ⚠️ THERE IS DELIBERATELY NO uid PARAMETER. The server binds the authorization
 * to `req.auth.uid` unconditionally — the CALLER. An enrol-for-someone-else path
 * would mint an authorization for a different uid, which is precisely the
 * delegation the checkpoint exists to prevent, and it would blind the detector:
 * "a factor appeared with an authorization someone else minted" is
 * indistinguishable from a plant. Possession proof is never delegated.
 *
 * ⚠️ ERRORS PROPAGATE — THERE IS NO try/catch HERE, DELIBERATELY. The caller's
 * fail-closed catch is the entire point: a mint that throws must abort the
 * ceremony before `setStep("phone")`, so the enrollment never proceeds without
 * an authorization behind it. Swallowing an error here would let the ceremony
 * continue and produce the unmatched factor described above.
 */
export const requestEnrollAuthorization = async (reauthProof: string): Promise<MfaEnrollAuthorizationResponse> => {
  const call = httpsCallable<MfaEnrollAuthorizationPayload, MfaEnrollAuthorizationResponse>(
    functions,
    'mfaEnrollAuthorization',
  );
  const res = await call({ reauthProof });
  return res.data;
};
